import React, { useEffect, useRef, useState } from 'react';
import 'react-dates/initialize';
import {
  CUSTOM_ARIA_LABELS,
  getRem,
  isMobileScreen,
  SHORTCUT_CHIPS_POSITIONS,
  START_DATE,
  END_DATE,
  useCustomPhrases,
} from 'lib/core';
import { DATE_FORMATS } from 'lib/date-input';
import { IconToday } from 'lib/icons';
import { isFocused, KEY_VALUES, useKeyDownEventListener, useShareForwardedRef, useWindowSize } from 'lib/utilities';
import moment from 'moment';
import PropTypes from 'prop-types';
import { DayPickerRangeController } from 'react-dates';
import { DayPickerPhrases } from 'react-dates/lib/defaultPhrases';
import momentPropTypes from 'react-moment-proptypes';
import styled, { css } from 'styled-components';
import { ChipDatePickerContainer, ChipShortcutContainer } from '../../elements';
import {
  getToday,
  getTomorrow,
  getYesterday,
  isAnyDateOutsideRange,
  isCalendarDayFocused,
  isDateBlocked,
  isDateInPast,
  isDateToday,
  MOMENT_DAYS,
  MOMENT_MONTH,
} from '../../utils';
import { Chip } from '../Chip';

const StyledChipShortcutChip = styled(Chip)`
  margin-top: ${({ theme }) => theme.size.spacing.medium.value};
  ${({ $isBottom }) =>
    $isBottom &&
    css`
      max-width: ${getRem('272px')};
      &:not(:last-child) {
        margin-right: ${({ theme }) => theme.size.spacing.medium.value};
      }
    `}
  ${({ $isRight }) =>
    $isRight &&
    css`
      display: flex;
      width: 100%;
    `}
`;

const ChipShortcutChip = React.forwardRef((props, ref) => <StyledChipShortcutChip {...props} ref={ref} />);

const DateRangePickerChips = React.forwardRef(
  (
    {
      allowDaysInPast = false,
      alwaysShowEndDate = false,
      ariaLabels = {
        chooseAvailableEndDate: CUSTOM_ARIA_LABELS.CHOOSE_AVAILABLE_END_DATE,
        chooseAvailableStartDate: CUSTOM_ARIA_LABELS.CHOOSE_AVAILABLE_START_DATE,
        jumpToNextMonth: CUSTOM_ARIA_LABELS.NEXT_MONTH,
        jumpToPrevMonth: CUSTOM_ARIA_LABELS.PREVIOUS_MONTH,
      },
      blockedDateRanges = [],
      className = '',
      dataTestId = undefined,
      dateFormat = DATE_FORMATS.MM_DD_YYYY,
      dateInputRef = undefined,
      defaultIsOpen = false,
      endDate = null,
      hideClearButton = false,
      highlightToday = false,
      id,
      label,
      minimumNights = 0,
      numberOfMonths = 2,
      onClose = () => {},
      onDatesChange,
      onOpen = () => {},
      onRemove = () => {},
      shortcutChipsPosition = SHORTCUT_CHIPS_POSITIONS.NONE,
      shortcutLabels = {
        nextMonth: 'Next Month',
        nextWeek: 'Next Week',
        today: 'Today',
        tomorrow: 'Tomorrow',
        yesterday: 'Yesterday',
      },
      startDate = null,
      ...other
    },
    ref,
  ) => {
    const chipInputRef = useShareForwardedRef(dateInputRef);
    const datePickerRef = useShareForwardedRef(ref);
    const lastShortcutChipRef = useRef();

    const [focusedInput, setFocusedInput] = useState(START_DATE);
    const [displayValue, setDisplayValue] = useState('');

    const [isCalendarOpen, setIsCalendarOpen] = useState(defaultIsOpen);
    const [isCalendarFocused, setIsCalendarFocused] = useState(false);
    const [isNavNextFocused, setIsNavNextFocused] = useState(false);
    const [isNavPrevFocused, setIsNavPrevFocused] = useState(false);

    const windowSize = useWindowSize();
    const numberOfMonthsShown = isMobileScreen(windowSize.width) ? 1 : numberOfMonths;

    const customPhrases = useCustomPhrases(ariaLabels, DayPickerPhrases);

    const shortcutChipsPositioning = isMobileScreen(windowSize.width)
      ? SHORTCUT_CHIPS_POSITIONS.BOTTOM
      : shortcutChipsPosition;

    const shouldRenderCalendarInfo = shortcutChipsPosition !== SHORTCUT_CHIPS_POSITIONS.NONE;

    // handles negative numbers as zero
    const nonNegativeMinimumNights = minimumNights < 0 ? 0 : minimumNights;

    /* -- Nav focus handlers -- */
    const handleFocusNavNext = () => {
      setIsNavPrevFocused(false);
      setIsNavNextFocused(true);
    };

    const handleFocusNavPrev = () => {
      setIsNavPrevFocused(true);
      setIsNavNextFocused(false);
    };

    /* -- calendar handlers -- */
    const openCalendar = () => {
      setIsCalendarOpen(true);
      onOpen();
    };

    const closeCalendar = () => {
      setIsCalendarOpen(false);
      setIsCalendarFocused(false);
      onClose();
    };

    const moveFocusToChip = () => {
      setTimeout(() => {
        return chipInputRef.current.focus();
      }, 0);
    };

    /* -- event listener helpers -- */

    const validateDateRange = () => {
      // check to see if a start date has been selected, but an end date has not been set
      if (startDate !== null && endDate === null) {
        // create a valid range based on minimumNights prop
        onDatesChange({ startDate, endDate: startDate.clone().add(nonNegativeMinimumNights, 'days') });
      }
    };

    const preventScroll = (event) => {
      event.preventDefault();
    };

    /* -- keyboard listeners -- */

    useKeyDownEventListener(
      KEY_VALUES.ESCAPE,
      () => {
        validateDateRange();
        closeCalendar();
        moveFocusToChip();
      },
      datePickerRef.current,
    );

    useKeyDownEventListener(
      KEY_VALUES.ARROW_DOWN,
      (event) => {
        preventScroll(event);

        if (isCalendarOpen) {
          setIsCalendarFocused(true);
        } else {
          openCalendar();
        }
      },
      datePickerRef.current,
    );

    useKeyDownEventListener(
      KEY_VALUES.SPACE,
      (event) => {
        const isNavigationIconFocused = isNavNextFocused || isNavPrevFocused;

        if (isNavigationIconFocused) {
          preventScroll(event);
          setIsNavPrevFocused(false);
          setIsNavNextFocused(false);
        }

        if (!isCalendarOpen) {
          event.preventDefault();
          openCalendar();
        }
      },
      datePickerRef.current,
    );

    useKeyDownEventListener(
      KEY_VALUES.TAB,
      (event) => {
        const shortcutChipsAreShown = shortcutChipsPosition !== SHORTCUT_CHIPS_POSITIONS.NONE;

        const isShiftKeyPressed = event.shiftKey;
        const isDateInputFocused = isFocused(chipInputRef.current);
        const isDayFocused = isCalendarDayFocused(datePickerRef);
        const isLastShortcutChipFocused = shortcutChipsAreShown && isFocused(lastShortcutChipRef.current);

        if (isDateInputFocused) {
          validateDateRange();
          closeCalendar();
          return;
        }

        if (isNavPrevFocused) {
          handleFocusNavNext();
          return;
        }

        if (isNavNextFocused) {
          if (isShiftKeyPressed) {
            handleFocusNavPrev();
            return;
          }

          setIsNavNextFocused(false);
          return;
        }

        if (isDayFocused) {
          if (isShiftKeyPressed) {
            handleFocusNavNext();
            return;
          }

          if (shortcutChipsAreShown) {
            return;
          }

          moveFocusToChip();
          validateDateRange();
          closeCalendar();
          return;
        }

        if (isLastShortcutChipFocused) {
          if (isShiftKeyPressed) {
            return;
          }

          moveFocusToChip();
          validateDateRange();
          closeCalendar();
        }
      },
      datePickerRef.current,
    );

    /* -- external change event listeners -- */

    useEffect(() => {
      // If both startDate and endDate are empty, clear the chip displayValue
      if (!startDate && !endDate) {
        setDisplayValue('');
        return;
      }

      // If just startDate
      if (startDate && !endDate) {
        if (isCalendarOpen) {
          // Selection is still in progress, show the dash
          setDisplayValue(`${startDate.format(dateFormat)} -`);
        } else {
          setDisplayValue(`${startDate.format(dateFormat)}`);
        }
        return;
      }

      // Have both startDate and endDate
      if (startDate && endDate) {
        if (moment(startDate).isSame(endDate) && !alwaysShowEndDate) {
          // if the dates are the same, only display one
          setDisplayValue(`${startDate.format(dateFormat)}`);
        } else {
          setDisplayValue(`${startDate.format(dateFormat)} - ${endDate.format(dateFormat)}`);
        }
      }

      /**
       * The condition !startDate && endDate is not currently handled.
       * The user shouldn't be able to get the component into this state,
       * so the developer would have to set explicitly set it. The risk
       * of this seems low. We should fix this, but it is not urgent.
       */
    }, [startDate, endDate, dateFormat, isCalendarOpen]);

    /* -- react-dates functions needed -- */

    const handleCalendarDayClick = ({ endDate, startDate }, isShortcutClick) => {
      if (focusedInput === END_DATE || isShortcutClick) {
        onDatesChange({ startDate, endDate });
      } else {
        onDatesChange({ startDate, endDate: null }); // clears end date on any new date range start selection
      }

      if ((startDate && endDate && focusedInput === END_DATE) || (startDate && endDate && isShortcutClick)) {
        closeCalendar();
        moveFocusToChip();
      }
    };

    const handleOutsideClick = (event) => {
      const elementClicked = event.target;
      const clickedOnDateInput = chipInputRef.current.contains(elementClicked);

      if (!clickedOnDateInput) {
        validateDateRange();
        closeCalendar();
      }
    };

    /* -- Shortcut Chip Handling -- */

    const renderShortcuts = () => {
      const handleShortcutClick = (newStartDate, newEndDate) => {
        handleCalendarDayClick({ startDate: newStartDate, endDate: newEndDate }, true); // true denotes shortcut chip interaction

        closeCalendar();
      };

      const handleTodayShortcutClick = () => {
        const today = getToday();

        handleShortcutClick(today, today);
      };

      const handleTomorrowShortcutClick = () => {
        const tomorrow = getTomorrow();

        handleShortcutClick(tomorrow, tomorrow);
      };

      const handleYesterdayShortcutClick = () => {
        const yesterday = getYesterday();

        handleShortcutClick(yesterday, yesterday);
      };

      const handleNextWeekShortcutClick = () => {
        const updatedStartDate = getToday();
        const updatedEndDate = moment(updatedStartDate).add(7, MOMENT_DAYS);

        handleShortcutClick(updatedStartDate, updatedEndDate);
      };

      const handleNextMonthShortcutClick = () => {
        const updatedStartDate = getToday();
        const updatedEndDate = moment(updatedStartDate).add(1, MOMENT_MONTH);

        handleShortcutClick(updatedStartDate, updatedEndDate);
      };

      const yesterdayShortcutChipProps = allowDaysInPast
        ? [
            {
              label: shortcutLabels.yesterday,
              onClick: handleYesterdayShortcutClick,
            },
          ]
        : [];

      const shortcutChipsProps = [
        {
          label: shortcutLabels.today,
          onClick: handleTodayShortcutClick,
        },
        {
          label: shortcutLabels.tomorrow,
          onClick: handleTomorrowShortcutClick,
        },
        ...yesterdayShortcutChipProps,
        {
          label: shortcutLabels.nextWeek,
          onClick: handleNextWeekShortcutClick,
        },
        {
          label: shortcutLabels.nextMonth,
          onClick: handleNextMonthShortcutClick,
        },
      ];

      const isBottom = isMobileScreen(windowSize.width) || shortcutChipsPosition === SHORTCUT_CHIPS_POSITIONS.BOTTOM;
      const isRight = !isMobileScreen(windowSize.width) && shortcutChipsPosition === SHORTCUT_CHIPS_POSITIONS.RIGHT;

      return (
        <ChipShortcutContainer isBottom={isBottom} isRight={isRight}>
          {shortcutChipsProps.map((chipProps, i) => (
            <ChipShortcutChip
              data-testid={dataTestId ? `${dataTestId}-shortcut-chip-${chipProps.label.replace(' ', '-')}` : undefined}
              $isBottom={isBottom}
              $isRight={isRight}
              key={chipProps.label}
              ref={i === shortcutChipsProps.length - 1 ? lastShortcutChipRef : undefined}
              {...chipProps}
            />
          ))}
        </ChipShortcutContainer>
      );
    };

    const handleClear = (event) => {
      // reset all internal state
      preventScroll(event);
      event.stopPropagation();
      onDatesChange({ startDate: null, endDate: null });
      setDisplayValue('');
      setFocusedInput(START_DATE);
      setIsCalendarFocused(false);
      moveFocusToChip();
      onRemove();
    };

    const handleFocusChange = () => {
      setFocusedInput(END_DATE);
    };

    // sets calendar Click focus to start date whenever calendar is closed
    useEffect(() => {
      if (!isCalendarOpen) {
        setFocusedInput(START_DATE);
      }
    }, [isCalendarOpen]);

    // properly focuses on the calendar after it is rendered, fixed a styling error
    useEffect(() => {
      if (!isCalendarOpen) return;

      const timer = setTimeout(() => {
        setIsCalendarFocused(true);
      }, 0);

      return () => clearTimeout(timer);
    }, [isCalendarOpen]);

    return (
      <ChipDatePickerContainer data-testid={dataTestId} id={id} ref={datePickerRef} className={className}>
        <Chip
          dataTestId={dataTestId}
          label={displayValue || label}
          onClick={isCalendarOpen ? closeCalendar : openCalendar}
          ref={chipInputRef}
          isOpen={isCalendarOpen}
          isSelected={!!displayValue}
          hasDropdown={hideClearButton || !displayValue}
          onRemove={!hideClearButton && displayValue ? handleClear : undefined}
          icon={<IconToday />}
        />
        {isCalendarOpen && (
          <DayPickerRangeController
            calendarInfoPosition={
              shortcutChipsPosition !== SHORTCUT_CHIPS_POSITIONS.NONE ? shortcutChipsPositioning : undefined
            }
            daySize={40}
            hideKeyboardShortcutsPanel
            horizontalMonthPadding={6}
            isDayBlocked={isDateBlocked(blockedDateRanges)}
            isDayHighlighted={highlightToday ? isDateToday : undefined}
            isFocused={isCalendarFocused}
            isOutsideRange={allowDaysInPast ? isAnyDateOutsideRange : isDateInPast}
            startDate={startDate}
            endDate={endDate}
            focusedInput={focusedInput}
            onFocusChange={handleFocusChange}
            minimumNights={nonNegativeMinimumNights}
            numberOfMonths={numberOfMonthsShown}
            onDatesChange={handleCalendarDayClick}
            onOutsideClick={handleOutsideClick}
            phrases={customPhrases}
            renderCalendarInfo={shouldRenderCalendarInfo ? renderShortcuts : undefined}
            onPrevMonthClick={handleFocusNavPrev}
            onNextMonthClick={handleFocusNavNext}
            {...other}
          />
        )}
      </ChipDatePickerContainer>
    );
  },
);

DateRangePickerChips.propTypes = {
  /** If true, it is allowed to pick days in past */
  allowDaysInPast: PropTypes.bool,
  /** If true, end date is always shown, even if it is the same as start date */
  alwaysShowEndDate: PropTypes.bool,
  /** Sets text that will be displayed for aria-labels required for screen readers */
  ariaLabels: PropTypes.shape({
    /** Text displayed for next month navigation arrow */
    jumpToNextMonth: PropTypes.node.isRequired,
    /** Text displayed for previous month navigation arrow */
    jumpToPrevMonth: PropTypes.node.isRequired,
  }),
  /** An array of date ranges that should be blocked for user to select. */
  blockedDateRanges: PropTypes.arrayOf(
    PropTypes.shape({
      /** Last day inclusive of blocked date range. */
      endDate: PropTypes.instanceOf(moment),
      /** First day inclusive of blocked date range. */
      startDate: PropTypes.instanceOf(moment),
    }),
  ),
  /** Adds additional class for datePicker */
  className: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Defines in what format date input should format value (e.g. YYYY/MM/DD, MM/DD/YYYY, DD/MM/YYYY) */
  dateFormat: PropTypes.oneOf(Object.values(DATE_FORMATS)),
  /** Props that are supplied to DateInput used in this component. */
  /** Access DOM element of date input field */
  dateInputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(HTMLDivElement) }),
  ]),
  /** If true, date picker is open on initial render */
  defaultIsOpen: PropTypes.bool,
  /** Value of date picker range end date. It's type is a moment object. Set it to null to reset the picker */
  endDate: momentPropTypes.momentObj,
  /** If true, the clear button is hidden, dropdown arrow always displays */
  hideClearButton: PropTypes.bool,
  /** If true, today on the calendar is highlighted */
  highlightToday: PropTypes.bool,
  /** Unique identifier to identify date picker component */
  id: PropTypes.string.isRequired,
  /** Label of the date chip field */
  label: PropTypes.node.isRequired,
  /** Defines the the minimum number of nights required. A value of 0 will allow users to select a range of today-today */
  minimumNights: PropTypes.number,
  /** Defines how many months should be shown on calendar */
  numberOfMonths: PropTypes.number,
  /** Callback that is called when date picker should close */
  onClose: PropTypes.func,
  /** Callback that is called when clicking remove on chip */
  onRemove: PropTypes.func,
  /** Callback that is called when date picker value changes.
   * It takes one argument: an object with properties of `startDate` and
   * `endDate` of type PropTypes.instanceOf(moment) */
  onDatesChange: PropTypes.func.isRequired,
  /** Callback that is called when date picker should open */
  onOpen: PropTypes.func,
  /** Sets where shortcut chips should be rendered */
  shortcutChipsPosition: PropTypes.oneOf(Object.values(SHORTCUT_CHIPS_POSITIONS)),
  /** Sets text that will be displayed for today, tomorrow and yesterday shortcuts */
  shortcutLabels: PropTypes.shape({
    /** Text displayed for next month shortcut */
    nextMonth: PropTypes.node.isRequired,
    /** Text displayed for next week shortcut */
    nextWeek: PropTypes.node.isRequired,
    /** Text displayed for today shortcut */
    today: PropTypes.node.isRequired,
    /** Text displayed for tomorrow shortcut */
    tomorrow: PropTypes.node.isRequired,
    /** Text displayed for yesterday shortcut */
    yesterday: PropTypes.node.isRequired,
  }),
  /** Value of date picker range start date. It's type is a moment object. Set it to null to reset the picker */
  startDate: momentPropTypes.momentObj,
};

export { DateRangePickerChips };
